import { Producer } from "features/settings/producers/producer";
import { Trader } from "features/settings/traders/trader";
import { AuditUser } from "./auditUser";
import Commodity from "./commodity";
import DeliveryPeriod from "./deliveryPeriod";
import PhysicalLocation from "./physicalLocation";
import FuturesContract from "./futuresContract";
import { DeliveryMethod } from "./deliveryMethod";
import { PaymentOption } from "./paymentOption";
import { Currency, CurrencyEnum } from "./currency";
import Offer from "./offer";
import { PositionSetting } from "domain/positionSetting";
import { MerchantThin } from "./merchant";
import { FeeThin } from "./fee";
import { CustomFieldThin } from "./customField";
import { ProducerAccount } from "features/settings/producers/producerAccount";
import { RelationshipType } from "./relationshipType";
import { ContractSignatureStatus } from "./contractSignatureStatus";
import ContractExtendedType from "./contractExtendedType";
import { ContractSignerType } from "./contractSignerType";

export default interface Contract {
    id: string;
    displayId: string;
    merchant: MerchantThin;
    internalId: number;
    erpId: string;
    erpDisplayId: string;
    isErpInSync: boolean;
    isErpIdManuallyEntered: boolean;
    isErpIdVerified: boolean;
    contractType: ContractType;
    contractSubType?: string;
    quantityContracted: number;
    workingQuantity: number;
    quantityPriced: number;
    remainingQuantity: number;
    quantityDelivered: number;
    cashPrice: number;
    finalPrice: number;
    futuresPrice: number;
    basis: number;
    isBasisLocked: boolean;
    sourceContractId?: number;
    sourceContractDisplayId?: string;
    sourceContractType?: ContractType;

    deliveryMethod: DeliveryMethod;
    freightFee?: number;
    freightFeeDisplayOnly?: number;
    paymentOption: PaymentOption;
    deferredPayDate?: string;
    interestStartDate?: string;
    interestRate?: number;
    contractExtendedType?: ContractExtendedType;
    customerAccount: ProducerAccount;
    finalPricingDate: Date;
    flag: boolean;
    comments: string;
    deliveryTerms?: string;
    customerContractId?: string;
    erpContractId?: string;

    producer: Producer;
    commodity: Commodity;
    currency: CurrencyEnum;
    deliveryPeriod: DeliveryPeriod;
    premium?: number;
    positionSetting: PositionSetting;
    deliveryPeriodStartDate: Date;
    deliveryPeriodEndDate: Date;
    futuresContract: FuturesContract;
    location: PhysicalLocation;
    inventoryLocation: PhysicalLocation;
    contractLocation: PhysicalLocation;
    region: PhysicalLocation;
    customerWillSell: boolean;
    trader: Trader;
    createdBy: AuditUser;
    createdDate: string;
    lastUpdatedBy: AuditUser;
    lastUpdatedDate: Date;
    offer: Offer;
    fees: FeeThin[];
    customFields: CustomFieldThin[];

    isReadOnly: boolean;
    disablePricings: boolean;
    canEditQuantity: boolean;
    isSupervisorEditing: boolean;
    disableErpSync: boolean;
    contractCurrency: Currency;
    isArchived: boolean;
    useUnappliedBalance?: boolean;

    isSyncingToErp: boolean;
    isRollInProgress: boolean;
    eSignatureStatus?: ContractSignatureStatus;
    eSignatureId?: number;
    eSignatureAwaitingSignerType?: ContractSignerType;
}

export interface ContractRelationship {
    sourceContractId: number;
    sourceContractDisplayId: string;
    targetContractId: number;
    targetContractDisplayId: string;
    relationshipType: RelationshipType;
}

export interface ContractSplit {
    id: number;
    quantity: number;
    useUnappliedBalance?: boolean;
    producer: Producer;
    customerAccount: ProducerAccount;
    deliveryLocation: PhysicalLocation;
    inventoryLocation: PhysicalLocation;
}

export enum ContractType {
    Cash = 1,
    Basis = 2,
    Hta = 3,
    DelayedPrice = 4,
    CashMinPrice = 5,
    HtaMinPrice = 6,
    BushelOnly = 7
}

export function getContractTypeName(contractType: ContractType): string {
    switch (contractType) {
        case ContractType.Cash:
            return "Cash";
        case ContractType.Hta:
            return "HTA";
        case ContractType.Basis:
            return "Basis";
        case ContractType.DelayedPrice:
            return "Delayed Price";
        case ContractType.CashMinPrice:
            return "Cash Min Price";
        case ContractType.HtaMinPrice:
            return "HTA Min Price";
        case ContractType.BushelOnly:
            return "Bushel Only";
        default:
            return "???";
    }
}